import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type {
  BudgetVersion, CooperationForm, EmployerCostEstimate, Engagement, Paginated,
  Person, ZusStatus,
} from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { BackButton } from "../components/BackButton";
import { formatMoney, parseMoney, sumMoney } from "../lib/money";
import { FORMY, NAZWY_SKLADEK, STATUSY, wymagaStatusu } from "../lib/payroll";

export function CrewPage() {
  const { code = "" } = useParams();
  const { can } = useAuth();
  const kolejka = useQueryClient();
  const [otwarty, setOtwarty] = useState(false);

  const { data } = useQuery({
    queryKey: ["engagements", code],
    queryFn: () => api.get<Paginated<Engagement>>(`/projects/${code}/engagements`),
  });
  const obsada = data?.results ?? [];

  const usun = useMutation({
    mutationFn: (id: string) => api.del(`/engagements/${id}`),
    onSuccess: () => kolejka.invalidateQueries({ queryKey: ["engagements", code] }),
  });

  const sumaBrutto = sumMoney(obsada.map((z) => z.amount_gross));
  const sumaKosztu = sumMoney(obsada.map((z) => z.employer_cost_estimate));
  const narzut = sumMoney([sumaKosztu, `-${sumaBrutto}`]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <BackButton fallback={`/projekty/${code}`} />
        <Typography variant="h6">{code} · obsada</Typography>
        <Box sx={{ flex: 1 }} />
        {can("commitment.create") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOtwarty(true)}>
            Dodaj do obsady
          </Button>
        )}
      </Stack>

      {/* Rzecz, dla której ten ekran istnieje: brutto wykonawcy i koszt
          pracodawcy to dwie różne liczby, a do kosztorysu wchodzi ta druga. */}
      {obsada.length > 0 && (
        <Alert severity={Number(narzut) > 0 ? "info" : "warning"}>
          Brutto wykonawców: {formatMoney(sumaBrutto)} zł.
          Koszt pracodawcy: <strong>{formatMoney(sumaKosztu)} zł</strong>.
          {Number(narzut) > 0
            ? ` Narzut ${formatMoney(narzut)} zł — to on wchodzi do kosztorysu, nie brutto.`
            : " Narzut zerowy — sprawdź, czy stawki składek są wprowadzone w panelu."}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <TableCell>Osoba</TableCell>
              <TableCell>Rola</TableCell>
              <TableCell>Forma</TableCell>
              <TableCell>Status ZUS</TableCell>
              <TableCell align="right">Ilość</TableCell>
              <TableCell align="right">Stawka</TableCell>
              <TableCell align="right">Brutto</TableCell>
              <TableCell align="right">Koszt pracodawcy</TableCell>
              <TableCell>Pozycja kosztorysu</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {obsada.map((z) => (
              <TableRow key={z.id} hover>
                <TableCell>{z.person_label}</TableCell>
                <TableCell>{z.role_name}</TableCell>
                <TableCell>{z.form_label}</TableCell>
                <TableCell>
                  {STATUSY.find((s) => s.value === z.zus_status)?.label ?? "—"}
                </TableCell>
                <TableCell align="right">{z.quantity} {z.unit}</TableCell>
                <TableCell align="right">{formatMoney(z.rate)}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatMoney(z.amount_gross)}
                </TableCell>
                <TableCell align="right"
                           sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                  <Tooltip title={Object.entries(z.cost_breakdown?.components ?? {})
                    .map(([k, v]) => `${NAZWY_SKLADEK[k] ?? k}: ${formatMoney(v)}`)
                    .join(" · ") || "Bez narzutu"}>
                    <span>{formatMoney(z.employer_cost_estimate)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{z.account_code ?? "—"}</TableCell>
                <TableCell align="right">
                  {can("commitment.create") && (
                    <IconButton size="small" onClick={() => usun.mutate(z.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {obsada.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Obsada nie jest jeszcze zaplanowana.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <DodajDoObsady open={otwarty} onClose={() => setOtwarty(false)} projectCode={code} />
    </Stack>
  );
}

function DodajDoObsady({
  open, onClose, projectCode,
}: { open: boolean; onClose: () => void; projectCode: string }) {
  const kolejka = useQueryClient();
  const [form, setForm] = useState({
    person: "", role_name: "", form: "zlecenie" as CooperationForm,
    zus_status: "brak" as ZusStatus, quantity: "1", unit: "dzień", rate: "",
    budget_line: "",
  });
  const [szacunek, setSzacunek] = useState<EmployerCostEstimate | null>(null);
  const [blad, setBlad] = useState<ApiError | null>(null);

  const { data: osoby } = useQuery({
    queryKey: ["people", ""],
    queryFn: () => api.get<Paginated<Person>>("/people"),
    enabled: open,
  });
  const { data: wersje } = useQuery({
    queryKey: ["budgets", projectCode],
    queryFn: () => api.get<Paginated<BudgetVersion>>(`/projects/${projectCode}/budgets`),
    enabled: open,
  });
  const wersjaId = (wersje?.results ?? []).find(
    (w) => w.status === "approved" || w.status === "locked")?.id
    ?? wersje?.results?.[0]?.id;
  const { data: wersja } = useQuery({
    queryKey: ["budget", wersjaId],
    queryFn: () => api.get<BudgetVersion>(`/budgets/${wersjaId}`),
    enabled: open && Boolean(wersjaId),
  });

  const brutto = sumMoney([
    String(Number(parseMoney(form.quantity) || 0) * Number(parseMoney(form.rate) || 0)),
  ]);

  // Podgląd na żywo: kierownik ma zobaczyć koszt, zanim wpisze kwotę
  // do kosztorysu, a nie po fakcie.
  useEffect(() => {
    if (!open || Number(brutto) <= 0) { setSzacunek(null); return; }
    let aktualne = true;
    api.post<EmployerCostEstimate>("/payroll/estimate", {
      amount_gross: brutto, form: form.form,
      zus_status: wymagaStatusu(form.form) ? form.zus_status : "nd",
      project: projectCode,
    }).then((s) => { if (aktualne) setSzacunek(s); }).catch(() => setSzacunek(null));
    return () => { aktualne = false; };
  }, [open, brutto, form.form, form.zus_status, projectCode]);

  const zapisz = useMutation({
    mutationFn: () => api.post<Engagement>(`/projects/${projectCode}/engagements`, {
      ...form,
      zus_status: wymagaStatusu(form.form) ? form.zus_status : "nd",
      quantity: parseMoney(form.quantity) || "1",
      rate: parseMoney(form.rate) || "0",
      budget_line: form.budget_line || null,
    }),
    onSuccess: () => {
      kolejka.invalidateQueries({ queryKey: ["engagements", projectCode] });
      setForm({ ...form, person: "", role_name: "", rate: "" });
      setBlad(null);
      onClose();
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Dodaj do obsady</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {blad && <Alert severity="error">{blad.message}</Alert>}

          <Stack direction="row" spacing={2}>
            <TextField select label="Osoba" value={form.person} sx={{ flex: 2 }}
                       onChange={(e) => setForm({ ...form, person: e.target.value })}>
              {(osoby?.results ?? []).map((o) => (
                <MenuItem key={o.id} value={o.id}>{o.last_name} {o.first_name}</MenuItem>
              ))}
            </TextField>
            <TextField label="Rola na planie" value={form.role_name} sx={{ flex: 2 }}
                       onChange={(e) => setForm({ ...form, role_name: e.target.value })} />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField select label="Forma współpracy" value={form.form} sx={{ flex: 1 }}
                       onChange={(e) =>
                         setForm({ ...form, form: e.target.value as CooperationForm })}>
              {FORMY.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
            </TextField>

            {/* Status pytamy tylko przy zleceniu — przy dziele i B2B nie ma
                czego rozgałęziać. */}
            {wymagaStatusu(form.form) && (
              <TextField select label="Status ubezpieczeniowy" sx={{ flex: 2 }}
                         value={form.zus_status}
                         helperText={STATUSY.find((s) => s.value === form.zus_status)?.hint}
                         onChange={(e) =>
                           setForm({ ...form, zus_status: e.target.value as ZusStatus })}>
                {STATUSY.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField label="Ilość" value={form.quantity} sx={{ width: 110 }}
                       onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            <TextField label="J.m." value={form.unit} sx={{ width: 110 }}
                       onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            <TextField label="Stawka" value={form.rate} sx={{ width: 140 }}
                       onChange={(e) => setForm({ ...form, rate: e.target.value })} />
            <TextField select label="Pozycja kosztorysu" sx={{ flex: 1 }}
                       value={form.budget_line}
                       onChange={(e) => setForm({ ...form, budget_line: e.target.value })}>
              <MenuItem value="">— nie przypisuj —</MenuItem>
              {(wersja?.lines ?? []).map((l) => (
                <MenuItem key={l.id ?? ""} value={l.id ?? ""}>
                  {l.account_code} — {l.description}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {szacunek && (
            <Alert severity={szacunek.profile_found && !szacunek.missing_rates.length
              ? "success" : "warning"}>
              <Stack spacing={0.5}>
                <span>
                  Brutto wykonawcy <strong>{formatMoney(szacunek.gross)} zł</strong>,
                  koszt pracodawcy <strong>{formatMoney(szacunek.total)} zł</strong>
                  {Number(szacunek.surcharge) > 0
                    && ` (narzut ${formatMoney(szacunek.surcharge)} zł)`}
                </span>
                {Object.keys(szacunek.components).length > 0 && (
                  <span style={{ fontSize: "0.85em" }}>
                    {Object.entries(szacunek.components)
                      .map(([k, v]) => `${NAZWY_SKLADEK[k] ?? k} ${formatMoney(v)}`)
                      .join(" · ")}
                  </span>
                )}
                {!szacunek.profile_found && (
                  <span>Nie ustalono, które składki obowiązują przy tej formie
                    i statusie — uzupełnij profil w panelu administracyjnym.</span>
                )}
                {szacunek.missing_rates.length > 0 && (
                  <span>Brak stawek dla: {szacunek.missing_rates
                    .map((k) => NAZWY_SKLADEK[k] ?? k).join(", ")}. Narzut jest
                    zaniżony, dopóki księgowa ich nie wprowadzi.</span>
                )}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={() => zapisz.mutate()}
                disabled={zapisz.isPending}>Dodaj</Button>
      </DialogActions>
    </Dialog>
  );
}
